import {
  ApprovalRequestId,
  type OrchestrationLatestTurn,
  type OrchestrationThreadActivity,
  type OrchestrationProposedPlanId,
  type ProviderKind,
  type UserInputQuestion,
  type ThreadId,
  type TurnId,
} from "@cmmd-forge/contracts";

import type {
  ChatMessage,
  ProposedPlan,
  SessionPhase,
  Thread,
  ThreadSession,
  TurnDiffSummary,
} from "./types";
import { addPlanStepDurations, planStateFromActivity } from "./planDurations";
import { workEntryIndicatesToolNeutralStatus, type WorkLogEntry } from "./workLogEntryDerivation";
import { deriveWorkLogEntries, compareActivitiesByOrder } from "./workLogActivityFiltering";
import { requestKindFromRequestType } from "./workLogEntryExtractors";
import {
  actionRequiredCtaLabel,
  buildGitHubActionRequiredRetryText,
  formatGitHubActionRequiredLabel,
  formatGitHubActionRequiredDetail,
  formatGitHubConfirmationDetail,
  formatGitHubOperationLabel,
  resolveGitHubActionRequiredDetail,
  sameGitHubActionRequired,
  extractGitHubActionRequiredRequest,
  type GitHubActionRequiredRequest,
  type GitHubToolConfirmationRequest,
} from "./workLogGitHub";
import {
  deriveTimelineEntriesWithState,
  isStreamingMessageTextUpdate,
  type TimelineEntriesProjection,
} from "./timelineEntriesProjection";

export type { TimelineEntriesProjection };

export type ProviderPickerKind = ProviderKind | "cursor";

export const PROVIDER_OPTIONS: Array<{
  value: ProviderPickerKind;
  label: string;
  available: boolean;
}> = [
  { value: "codex", label: "Codex", available: true },
  { value: "claudeAgent", label: "Claude", available: true },
  { value: "cmmd", label: "CMMD", available: true },
  { value: "ollama", label: "Ollama", available: false },
  { value: "cursor", label: "Cursor", available: false },
];

// ---------------------------------------------------------------------------
// Contract vocabulary. The forge shapes are the stable surface: the work-log
// entry lives in workLogEntryDerivation (tone keeps "approval", the brokered
// GitHub blocker fields stay), the GitHub blocker types in workLogGitHub, and
// the timeline projection in timelineEntriesProjection. Everything here is
// re-exported so the import surface for the app is still this module.
// ---------------------------------------------------------------------------

export type { WorkLogEntry, GitHubActionRequiredRequest, GitHubToolConfirmationRequest };
export {
  deriveWorkLogEntries,
  workEntryIndicatesToolNeutralStatus,
  isStreamingMessageTextUpdate,
  actionRequiredCtaLabel,
  buildGitHubActionRequiredRetryText,
  formatGitHubActionRequiredLabel,
  formatGitHubActionRequiredDetail,
  resolveGitHubActionRequiredDetail,
  sameGitHubActionRequired,
};
export {
  workEntryDisplayIndicatesToolFailure,
  workEntryIndicatesToolSuccess,
  workLogEntryIsToolLike,
} from "./workLogToolPresentation";

export interface PendingApproval {
  requestId: ApprovalRequestId;
  requestKind: "command" | "file-read" | "file-change";
  createdAt: string;
  detail?: string;
}

export interface PendingUserInput {
  requestId: ApprovalRequestId;
  createdAt: string;
  questions: ReadonlyArray<UserInputQuestion>;
}

export interface ActivePlanState {
  createdAt: string;
  turnId: TurnId | null;
  explanation?: string | null;
  steps: Array<{
    step: string;
    status: "pending" | "inProgress" | "completed";
    /** Upstream parity (ticket 3522): how long the step took, from snapshot history. */
    durationMs?: number;
  }>;
}

export interface LatestProposedPlanState {
  id: OrchestrationProposedPlanId;
  createdAt: string;
  updatedAt: string;
  turnId: TurnId | null;
  planMarkdown: string;
  implementedAt: string | null;
  implementationThreadId: ThreadId | null;
}

export type TimelineEntry =
  | {
      id: string;
      kind: "message";
      createdAt: string;
      message: ChatMessage;
    }
  | {
      id: string;
      kind: "proposed-plan";
      createdAt: string;
      proposedPlan: ProposedPlan;
    }
  | {
      id: string;
      kind: "work";
      createdAt: string;
      entry: WorkLogEntry;
    };

export function formatDuration(durationMs: number): string {
  if (!Number.isFinite(durationMs) || durationMs < 0) return "0ms";
  if (durationMs < 1_000) return `${Math.max(1, Math.round(durationMs))}ms`;
  if (durationMs < 10_000) return `${(durationMs / 1_000).toFixed(1)}s`;
  if (durationMs < 60_000) return `${Math.round(durationMs / 1_000)}s`;
  const minutes = Math.floor(durationMs / 60_000);
  const seconds = Math.round((durationMs % 60_000) / 1_000);
  if (seconds === 0) return `${minutes}m`;
  if (seconds === 60) return `${minutes + 1}m`;
  return `${minutes}m ${seconds}s`;
}

export function formatElapsed(startIso: string, endIso: string | undefined): string | null {
  if (!endIso) return null;
  const startedAt = Date.parse(startIso);
  const endedAt = Date.parse(endIso);
  if (Number.isNaN(startedAt) || Number.isNaN(endedAt) || endedAt < startedAt) {
    return null;
  }
  return formatDuration(endedAt - startedAt);
}

type LatestTurnTiming = Pick<OrchestrationLatestTurn, "turnId" | "startedAt" | "completedAt">;
type SessionActivityState = Pick<ThreadSession, "orchestrationStatus" | "activeTurnId">;

export function isLatestTurnSettled(
  latestTurn: LatestTurnTiming | null,
  session: SessionActivityState | null,
): boolean {
  if (!latestTurn?.startedAt) return false;
  if (!latestTurn.completedAt) return false;
  if (!session) return true;
  if (session.orchestrationStatus === "running") return false;
  return true;
}

export function deriveActiveWorkStartedAt(
  latestTurn: LatestTurnTiming | null,
  session: SessionActivityState | null,
  sendStartedAt: string | null,
): string | null {
  const runningTurnId =
    session?.orchestrationStatus === "running" ? (session.activeTurnId ?? null) : null;
  if (runningTurnId !== null) {
    if (latestTurn?.turnId === runningTurnId) {
      return latestTurn.startedAt ?? sendStartedAt;
    }
    return sendStartedAt;
  }
  if (!isLatestTurnSettled(latestTurn, session)) {
    return latestTurn?.startedAt ?? sendStartedAt;
  }
  return sendStartedAt;
}

function isStalePendingRequestFailureDetail(detail: string | undefined): boolean {
  const normalized = detail?.toLowerCase();
  if (!normalized) {
    return false;
  }
  return (
    normalized.includes("stale pending approval request") ||
    normalized.includes("stale pending user-input request") ||
    normalized.includes("unknown pending approval request") ||
    normalized.includes("unknown pending permission request") ||
    normalized.includes("unknown pending user-input request")
  );
}

export function derivePendingApprovals(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): PendingApproval[] {
  const openByRequestId = new Map<ApprovalRequestId, PendingApproval>();
  const ordered = [...activities].toSorted(compareActivitiesByOrder);

  for (const activity of ordered) {
    const payload =
      activity.payload && typeof activity.payload === "object"
        ? (activity.payload as Record<string, unknown>)
        : null;
    const requestId =
      payload && typeof payload.requestId === "string"
        ? ApprovalRequestId.make(payload.requestId)
        : null;
    const requestKind =
      payload &&
      (payload.requestKind === "command" ||
        payload.requestKind === "file-read" ||
        payload.requestKind === "file-change")
        ? payload.requestKind
        : payload
          ? requestKindFromRequestType(payload.requestType)
          : null;
    const detail = payload && typeof payload.detail === "string" ? payload.detail : undefined;

    if (activity.kind === "approval.requested" && requestId && requestKind) {
      openByRequestId.set(requestId, {
        requestId,
        requestKind,
        createdAt: activity.createdAt,
        ...(detail ? { detail } : {}),
      });
      continue;
    }

    if (activity.kind === "approval.resolved" && requestId) {
      openByRequestId.delete(requestId);
      continue;
    }

    if (
      activity.kind === "provider.approval.respond.failed" &&
      requestId &&
      isStalePendingRequestFailureDetail(detail)
    ) {
      openByRequestId.delete(requestId);
      continue;
    }
  }

  return [...openByRequestId.values()].toSorted((left, right) =>
    left.createdAt.localeCompare(right.createdAt),
  );
}

function parseUserInputQuestions(
  payload: Record<string, unknown> | null,
): ReadonlyArray<UserInputQuestion> | null {
  const questions = payload?.questions;
  if (!Array.isArray(questions)) {
    return null;
  }
  const parsed = questions
    .map<UserInputQuestion | null>((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const question = entry as Record<string, unknown>;
      if (
        typeof question.id !== "string" ||
        typeof question.header !== "string" ||
        typeof question.question !== "string" ||
        !Array.isArray(question.options)
      ) {
        return null;
      }
      const options = question.options
        .map<UserInputQuestion["options"][number] | null>((option) => {
          if (!option || typeof option !== "object") return null;
          const optionRecord = option as Record<string, unknown>;
          if (
            typeof optionRecord.label !== "string" ||
            typeof optionRecord.description !== "string"
          ) {
            return null;
          }
          return {
            label: optionRecord.label,
            description: optionRecord.description,
          };
        })
        .filter((option): option is UserInputQuestion["options"][number] => option !== null);
      if (options.length === 0) {
        return null;
      }
      return {
        id: question.id,
        header: question.header,
        question: question.question,
        options,
        multiSelect: question.multiSelect === true,
      };
    })
    .filter((question): question is UserInputQuestion => question !== null);
  return parsed.length > 0 ? parsed : null;
}

export function derivePendingUserInputs(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): PendingUserInput[] {
  const openByRequestId = new Map<ApprovalRequestId, PendingUserInput>();
  const ordered = [...activities].toSorted(compareActivitiesByOrder);

  for (const activity of ordered) {
    const payload =
      activity.payload && typeof activity.payload === "object"
        ? (activity.payload as Record<string, unknown>)
        : null;
    const requestId =
      payload && typeof payload.requestId === "string"
        ? ApprovalRequestId.make(payload.requestId)
        : null;
    const detail = payload && typeof payload.detail === "string" ? payload.detail : undefined;

    if (activity.kind === "user-input.requested" && requestId) {
      const questions = parseUserInputQuestions(payload);
      if (!questions) {
        continue;
      }
      openByRequestId.set(requestId, {
        requestId,
        createdAt: activity.createdAt,
        questions,
      });
      continue;
    }

    if (activity.kind === "user-input.resolved" && requestId) {
      openByRequestId.delete(requestId);
      continue;
    }

    if (
      activity.kind === "provider.user-input.respond.failed" &&
      requestId &&
      isStalePendingRequestFailureDetail(detail)
    ) {
      openByRequestId.delete(requestId);
    }
  }

  return [...openByRequestId.values()].toSorted((left, right) =>
    left.createdAt.localeCompare(right.createdAt),
  );
}

/**
 * The GitHub/CMMD setup the agent is currently blocked on (connect GitHub, sign
 * back in, link a repo, …), surfaced composer-anchored like Claude/Codex — or
 * null when nothing blocks the latest turn.
 *
 * There is no explicit "action resolved" event, so activeness is turn-scoped
 * (same approach as deriveActivePlanState): the blocker is active only while the
 * most recent action-required belongs to the LATEST turn. Resolving it and
 * retrying sends a new user turn, which advances latestTurnId past this one and
 * clears the panel — so it can never get stuck on a resolved ask. When the turn
 * is unknown we return null and let the transcript row remain the backstop,
 * rather than risk a stuck composer panel.
 */
export function deriveActiveActionRequired(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  latestTurnId: TurnId | undefined,
): GitHubActionRequiredRequest | null {
  if (!latestTurnId) return null;
  const ordered = [...activities].toSorted(compareActivitiesByOrder);
  let latest: { request: GitHubActionRequiredRequest; turnId: TurnId | null } | null = null;
  for (const activity of ordered) {
    const payload =
      activity.payload && typeof activity.payload === "object"
        ? (activity.payload as Record<string, unknown>)
        : null;
    const request = extractGitHubActionRequiredRequest(payload);
    if (request) latest = { request, turnId: activity.turnId };
  }
  if (!latest || latest.turnId !== latestTurnId) return null;
  return latest.request;
}

export function deriveActivePlanState(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  latestTurnId: TurnId | undefined,
): ActivePlanState | null {
  const ordered = [...activities].toSorted(compareActivitiesByOrder);
  const allPlanActivities = ordered.filter((activity) => activity.kind === "turn.plan.updated");
  // Prefer plan from the current turn; fall back to the most recent plan from any turn
  // so that TodoWrite tasks persist across follow-up messages.
  const latest =
    (latestTurnId
      ? allPlanActivities.findLast((activity) => activity.turnId === latestTurnId)
      : undefined) ??
    allPlanActivities.at(-1) ??
    null;
  if (!latest) {
    return null;
  }
  const plan = planStateFromActivity(latest);
  if (!plan) return null;
  // Upstream parity (ticket 3522): completed steps carry the duration their
  // snapshot history observed, timed from the last clear of the plan.
  const matchingActivities = allPlanActivities.filter(
    (activity) => activity.turnId === latest.turnId,
  );
  const latestClearIndex = matchingActivities.findLastIndex(
    (activity) => planStateFromActivity(activity) === null,
  );
  const timedPlan = addPlanStepDurations(plan, matchingActivities.slice(latestClearIndex + 1));
  // Forge keeps its own settling rule on top: a turn that completed after the
  // latest snapshot marks its in-progress steps completed.
  const turnCompletedAfterPlan =
    latest.turnId !== null &&
    ordered.some(
      (activity) =>
        activity.kind === "turn.completed" &&
        activity.turnId === latest.turnId &&
        compareActivitiesByOrder(latest, activity) < 0,
    );
  return {
    ...timedPlan,
    steps: turnCompletedAfterPlan
      ? timedPlan.steps.map((step) =>
          step.status === "inProgress" ? { ...step, status: "completed" as const } : step,
        )
      : timedPlan.steps,
  };
}

export function findLatestProposedPlan(
  proposedPlans: ReadonlyArray<ProposedPlan>,
  latestTurnId: TurnId | string | null | undefined,
): LatestProposedPlanState | null {
  if (latestTurnId) {
    const matchingTurnPlan = [...proposedPlans]
      .filter((proposedPlan) => proposedPlan.turnId === latestTurnId)
      .toSorted(
        (left, right) =>
          left.updatedAt.localeCompare(right.updatedAt) || left.id.localeCompare(right.id),
      )
      .at(-1);
    if (matchingTurnPlan) {
      return toLatestProposedPlanState(matchingTurnPlan);
    }
  }

  const latestPlan = [...proposedPlans]
    .toSorted(
      (left, right) =>
        left.updatedAt.localeCompare(right.updatedAt) || left.id.localeCompare(right.id),
    )
    .at(-1);
  if (!latestPlan) {
    return null;
  }

  return toLatestProposedPlanState(latestPlan);
}

export function findSidebarProposedPlan(input: {
  threads: ReadonlyArray<Pick<Thread, "id" | "proposedPlans">>;
  latestTurn: Pick<OrchestrationLatestTurn, "turnId" | "sourceProposedPlan"> | null;
  latestTurnSettled: boolean;
  threadId: ThreadId | string | null | undefined;
}): LatestProposedPlanState | null {
  const activeThreadPlans =
    input.threads.find((thread) => thread.id === input.threadId)?.proposedPlans ?? [];

  if (!input.latestTurnSettled) {
    const sourceProposedPlan = input.latestTurn?.sourceProposedPlan;
    if (sourceProposedPlan) {
      const sourcePlan = input.threads
        .find((thread) => thread.id === sourceProposedPlan.threadId)
        ?.proposedPlans.find((plan) => plan.id === sourceProposedPlan.planId);
      if (sourcePlan) {
        return toLatestProposedPlanState(sourcePlan);
      }
    }
  }

  return findLatestProposedPlan(activeThreadPlans, input.latestTurn?.turnId ?? null);
}

export function hasActionableProposedPlan(
  proposedPlan: LatestProposedPlanState | Pick<ProposedPlan, "implementedAt"> | null,
): boolean {
  return proposedPlan !== null && proposedPlan.implementedAt === null;
}

export function hasToolActivityForTurn(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  turnId: TurnId | null | undefined,
): boolean {
  if (!turnId) return false;
  return activities.some((activity) => activity.turnId === turnId && activity.tone === "tool");
}

/**
 * Ordered timeline entries through the stream-reusing projection, plus forge's
 * own text confirmations: brokered GitHub approval asks that exist only as
 * assistant prose are promoted to work rows here, so they render (and stay
 * approvable) even when no tool activity carries them.
 */
// Text-confirmation rows are re-derived on every call, but a streaming
// projection needs their object identity stable per message so row reuse
// downstream does not see churn. Keyed by the message object; refreshed when
// the confirmation id changes.
const textConfirmationRowsByMessage = new WeakMap<ChatMessage, TimelineEntry[]>();

function textConfirmationRowsForMessage(
  message: ChatMessage,
  workEntries: ReadonlyArray<WorkLogEntry>,
): TimelineEntry[] {
  if (message.role !== "assistant") return [];
  const confirmation = extractGitHubConfirmationFromAssistantText(message.text, {
    workEntries,
    createdAt: message.createdAt,
  });
  if (!confirmation) return [];
  const cached = textConfirmationRowsByMessage.get(message);
  const id = `${message.id}:github-confirmation:${confirmation.confirmationId}`;
  if (cached && cached[0]?.id === id) return cached;
  const row: TimelineEntry = {
    id,
    kind: "work" as const,
    createdAt: message.createdAt,
    entry: {
      id,
      createdAt: message.createdAt,
      label: "GitHub action needs approval",
      detail: formatGitHubConfirmationDetail(confirmation),
      tone: "approval" as const,
      toolTitle: formatGitHubOperationLabel(confirmation.operation),
      githubConfirmation: confirmation,
    },
  };
  const rows = [row];
  textConfirmationRowsByMessage.set(message, rows);
  return rows;
}

/**
 * Projection-preserving derivation: returns the full projection (inputs plus
 * merged entries, text confirmations included) so callers can feed it back as
 * `previous` on the next render. Confirmation rows already present in
 * `previous.entries` are reused — only ids missing from the reused projection
 * are appended, so a streaming update never duplicates an approval card.
 */
export function deriveTimelineEntriesProjection(
  messages: ChatMessage[],
  proposedPlans: ProposedPlan[],
  workEntries: WorkLogEntry[],
  previous?: TimelineEntriesProjection | null,
): TimelineEntriesProjection {
  const projection = deriveTimelineEntriesWithState(messages, proposedPlans, workEntries, previous);
  const textConfirmationRows: TimelineEntry[] = messages.flatMap((message) =>
    textConfirmationRowsForMessage(message, workEntries),
  );
  if (textConfirmationRows.length === 0) {
    return projection;
  }
  const presentIds = new Set(projection.entries.map((entry) => entry.id));
  const additions = textConfirmationRows.filter((row) => !presentIds.has(row.id));
  if (additions.length === 0) {
    return projection;
  }
  return {
    ...projection,
    entries: [...projection.entries, ...additions].toSorted((a, b) =>
      a.createdAt.localeCompare(b.createdAt),
    ),
  };
}

export function deriveTimelineEntries(
  messages: ChatMessage[],
  proposedPlans: ProposedPlan[],
  workEntries: WorkLogEntry[],
  previous?: TimelineEntriesProjection | null,
): TimelineEntry[] {
  return deriveTimelineEntriesProjection(messages, proposedPlans, workEntries, previous).entries;
}

function extractGitHubConfirmationFromAssistantText(
  text: string,
  context?: {
    readonly workEntries: ReadonlyArray<WorkLogEntry>;
    readonly createdAt: string;
  },
): GitHubToolConfirmationRequest | null {
  if (!text.includes("confirm_github_tool")) return null;
  const confirmationId =
    matchFirst(
      text,
      /\b(?:New confirmation id|Confirmation id):\s*`?([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})`?/i,
    ) ?? null;
  const repoMatch =
    /\bRepo(?: to create)?:\s*(private|public|internal)\s+`?([A-Za-z0-9_.-]+)\/([A-Za-z0-9_.-]+)`?/i.exec(
      text,
    );
  if (!confirmationId) return null;
  const refreshCredentialsConfirmation = extractRefreshCredentialsConfirmationFromAssistantText(
    text,
    confirmationId,
    context,
  );
  if (refreshCredentialsConfirmation) return refreshCredentialsConfirmation;
  if (!repoMatch) return null;
  const visibility = repoMatch[1]?.toLowerCase();
  const owner = repoMatch[2];
  const name = repoMatch[3];
  if (!owner || !name) return null;
  return {
    confirmationId,
    toolName: "forge_github_create_repository",
    operation: "create_repository",
    request: {
      owner,
      name,
      visibility,
    },
  };
}

function extractRefreshCredentialsConfirmationFromAssistantText(
  text: string,
  confirmationId: string,
  context:
    | {
        readonly workEntries: ReadonlyArray<WorkLogEntry>;
        readonly createdAt: string;
      }
    | undefined,
): GitHubToolConfirmationRequest | null {
  if (!mentionsRefreshCredentialsConfirmation(text)) return null;
  const latestRefreshConfirmation = findLatestGitHubConfirmationWorkEntry(
    context?.workEntries ?? [],
    context?.createdAt,
    "forge_github_refresh_credentials",
  );
  const request = latestRefreshConfirmation?.request;
  const cwd = request ? trimToNull(request.cwd) : null;
  if (!cwd) return null;
  return {
    confirmationId,
    toolName: "forge_github_refresh_credentials",
    operation: "refresh_credentials",
    request: { cwd },
  };
}

function mentionsRefreshCredentialsConfirmation(text: string): boolean {
  return (
    /\bforge_github_refresh_credentials\b/i.test(text) ||
    /\bcredential refresh\b/i.test(text) ||
    /\brefresh(?:ing)? GitHub credentials\b/i.test(text)
  );
}

function findLatestGitHubConfirmationWorkEntry(
  workEntries: ReadonlyArray<WorkLogEntry>,
  createdAt: string | undefined,
  toolName: string,
): GitHubToolConfirmationRequest | null {
  for (const entry of workEntries.toReversed()) {
    if (createdAt !== undefined && entry.createdAt > createdAt) continue;
    const confirmation = entry.githubConfirmation;
    if (confirmation?.toolName === toolName) return confirmation;
  }
  return null;
}

function trimToNull(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function matchFirst(text: string, pattern: RegExp): string | null {
  return pattern.exec(text)?.[1] ?? null;
}

export function inferCheckpointTurnCountByTurnId(
  summaries: TurnDiffSummary[],
): Record<TurnId, number> {
  const sorted = [...summaries].toSorted((a, b) => a.completedAt.localeCompare(b.completedAt));
  const result: Record<TurnId, number> = {};
  for (let index = 0; index < sorted.length; index += 1) {
    const summary = sorted[index];
    if (!summary) continue;
    result[summary.turnId] = index + 1;
  }
  return result;
}

export function derivePhase(session: ThreadSession | null): SessionPhase {
  if (!session || session.status === "closed") return "disconnected";
  if (session.status === "connecting") return "connecting";
  if (session.status === "running") return "running";
  return "ready";
}

export function deriveActivePendingGitHubConfirmation(
  timelineEntries: ReadonlyArray<TimelineEntry>,
  phase: SessionPhase,
): GitHubToolConfirmationRequest | null {
  // Show overlay in "running" (agent mid-turn) and "ready" (agent turn ended, waiting for user)
  // but not while disconnected or connecting (no backend to approve against).
  if (phase === "disconnected" || phase === "connecting") return null;
  for (const entry of timelineEntries.toReversed()) {
    if (entry.kind === "work" && entry.entry.githubConfirmation) {
      return entry.entry.githubConfirmation;
    }
  }
  return null;
}

function toLatestProposedPlanState(proposedPlan: ProposedPlan): LatestProposedPlanState {
  return {
    id: proposedPlan.id,
    createdAt: proposedPlan.createdAt,
    updatedAt: proposedPlan.updatedAt,
    turnId: proposedPlan.turnId,
    planMarkdown: proposedPlan.planMarkdown,
    implementedAt: proposedPlan.implementedAt,
    implementationThreadId: proposedPlan.implementationThreadId,
  };
}
